import { useCallback, useContext, useEffect } from "react";

import { usePostHog } from "posthog-js/react";

import { subscriptionContext } from "context";
import { hasPaidCarrierSubscription, userBelongsToCompany } from "utils";

import {
  clearLocalSubscription,
  getLocalSubscription,
  setLocalSubscription,
} from "../clientData";
import { useAuthContext } from "./useAuthContext";
import { useSwrFetch } from "./useSwrFetch";

export function useSubscriptionStatus() {
  const { subscription, dispatch } = useContext(subscriptionContext);
  const { isAuthenticated, user } = useAuthContext();
  const posthog = usePostHog();

  // Generate URL conditionally - SWR won't fetch if null
  const companyId = user?.roles[0]?.company?.id;
  const url =
    isAuthenticated && user && userBelongsToCompany(user) && companyId
      ? `/subscription/condensed/company/${companyId}`
      : null;

  const { data: fetchedSubscription, error: subscriptionError } =
    useSwrFetch<CondensedSubscription>({
      url,
      errorMessage: "Failed to load subscription status",
    });

  const setSubscription = useCallback(
    (newSubscription: CondensedSubscription) => {
      dispatch({ type: "setSubscription", subscription: newSubscription });
      setLocalSubscription(newSubscription);
    },
    [dispatch],
  );

  const clearSubscription = useCallback(() => {
    dispatch({ type: "clearSubscription" });
    clearLocalSubscription();
  }, [dispatch]);

  // Handle SWR data -> context sync
  useEffect(() => {
    if (fetchedSubscription) {
      setSubscription(fetchedSubscription);
      // PostHog integration (keep existing logic)
      if (posthog && companyId) {
        posthog?.group("COMPANY", companyId.toString(), {
          subscription_plan: fetchedSubscription.subscription_plan,
          // Lets any company-scoped insight split lapsed companies by why they
          // lapsed, without joining against the banner's own events.
          inactivity_reason: fetchedSubscription.inactivity_reason,
        });
      }
    }
  }, [fetchedSubscription, setSubscription, posthog, companyId]);

  /*
  useEffect runs whenever subscription in context is null
  and user is authenticated. Its task is to always maintain
  the subscription context populated.
  */
  useEffect(() => {
    if (user && isAuthenticated && subscription == null) {
      const localSubscription = getLocalSubscription();
      if (localSubscription) {
        // subscription in local storage -> restore to in-memory subscription context
        setSubscription(localSubscription);
      }
      // If no local storage, SWR will fetch when URL becomes available
    }
  }, [user, isAuthenticated, subscription, setSubscription]);

  /*
  Runs whenever user becomes unauthenticated. Clears local storage and
  and context for subscription info
   */
  useEffect(() => {
    if (user == null || !isAuthenticated) {
      clearSubscription();
    }
  }, [user, isAuthenticated, clearSubscription]);

  const isPayingCarrier = hasPaidCarrierSubscription(subscription);

  return {
    subscription,
    isPayingCarrier,
    clearSubscription,
    error: subscriptionError,
  };
}
